import type { ImagePinComponent as ImagePinComponentType } from '@lessonup/pins-shared';
import { Box, LayoutPartDict, Line, Pin, PinComponent } from '@lessonup/pins-shared';
import { color, css, isLeftMBClick, isRightMBClick, styled } from '@lessonup/ui-components';
import React, { memo, useCallback } from 'react';
import { colorSets } from '../../foundations/colorSets/colorSets';
import {
  OnMouseDownHandler,
  OnMouseEnterHandler,
  OnMouseLeaveHandler,
  OnTouchStartHandler,
} from '../../hooks/useHandleMouseEvents/useHandleMouseEvents.types';
import { AnswerAreaPinComponent } from '../../pinComponents/AnswerAreaPinComponent/AnswerAreaPinComponent';
import { AnswerAreaContentSwitch } from '../../pinComponents/AnswerAreaPinComponent/content/AnswerAreaContentSwitch';
import { AudioPinComponent } from '../../pinComponents/AudioPinComponent/AudioPinComponent';
import { CategoryAreaPinComponent } from '../../pinComponents/CategoryAreaPinComponent/CategoryAreaPinComponent';
import { DropZonePinComponent } from '../../pinComponents/DropZonePinComponent/DropZonePinComponent';
import { FillInTheBlanksAreaPinComponent } from '../../pinComponents/FillInTheBlankAreaPinComponent/FillInTheBlanksAreaPinComponent';
import { HotspotPinComponent } from '../../pinComponents/HotspotPinComponent/HotspotPinComponent';
import { ImagePinComponent } from '../../pinComponents/ImagePinComponent/ImagePinComponent';
import { LinePinComponent } from '../../pinComponents/LinePinComponent/LinePinComponent';
import { ShapePinComponent } from '../../pinComponents/ShapePinComponent/ShapePinComponent';
import { UneditableShapePinComponent } from '../../pinComponents/ShapePinComponent/UneditableShapePinComponent';
import { SpinnerPinComponent } from '../../pinComponents/SpinnerPinComponent/SpinnerPinComponent';
import { TextPinComponent } from '../../pinComponents/TextPinComponent/TextPinComponent';
import { TimerPinComponent } from '../../pinComponents/TimerPinComponent/TimerPinComponent';
import { UnsupportedPinComponent } from '../../pinComponents/UnsupportedPinComponent/UnsupportedPinComponent';
import { VideoPinComponent } from '../../pinComponents/VideoPinComponent/VideoPinComponent';
import { isLineLayout } from '../../types/pinComponents/pinComponentLayouts';
import { usePinEditorContextMenu } from '../../utils/contextMenu/PinEditorContextMenuContext';
import { boxFromLayout, getBoxForGroupAction } from '../../utils/geometry';
import { AnimationTag } from '../AnimationTag';
import { TopAligned } from '../TopAligned/TopAligned';

export interface PinComponentSwitchProps {
  pin: Pin;
  pinComponent: PinComponent;
  editable: boolean;
  layoutParts?: LayoutPartDict;
  scale: number;
  activePinComponents?: PinComponent[];
  isStatic?: boolean;
  isThumb?: boolean;
  onMouseDown?: OnMouseDownHandler;
  onTouchStart?: OnTouchStartHandler;
  onMouseEnter?: OnMouseEnterHandler;
  onMouseLeave?: OnMouseLeaveHandler;
  isHidden?: boolean;
  animationStep?: number;
  Renderer?: React.FC<PinComponentSwitchRendererProps>;
  isTextEditMode?: boolean;
  setModeTextEdit?(textPinComponentId: string, categoryId?: string): void;
  setModeCropping?(imagePinComponent: ImagePinComponentType): void;
  setModeCategoryEdit?(categoryAreaPinComponentId: string, categoryId: string): void;
  activeCategoryId?: string;
  isMoveActionInProgress?: boolean;
  className?: string;
  onSaveCategory?: (categoryId: string, newText: string) => void;
  active: boolean;
  highlighted?: boolean;
  showAsAnimated?: boolean;
}

export const PinComponentSwitch: React.FC<PinComponentSwitchProps> = ({
  pin,
  pinComponent,
  editable,
  isHidden,
  animationStep,
  active,
  activePinComponents,
  layoutParts,
  isStatic = true,
  isThumb,
  onMouseDown,
  onTouchStart,
  onMouseEnter,
  onMouseLeave,
  Renderer = PinComponentSwitchDefaultRendererMemo,
  scale,
  isTextEditMode,
  setModeTextEdit,
  setModeCropping,
  setModeCategoryEdit,
  activeCategoryId,
  isMoveActionInProgress,
  className,
  onSaveCategory,
  highlighted = false,
  showAsAnimated,
}) => {
  const isPartOfGroup =
    (activePinComponents?.length ?? 0) > 1 && activePinComponents?.some((c) => c.id === pinComponent.id);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      isLeftMBClick(event) && !isPartOfGroup
        ? onMouseDown?.(event, { action: 'move', componentId: pinComponent.id })
        : isLeftMBClick(event) &&
          onMouseDown?.(event, { action: 'move-group', groupBox: getBoxForGroupAction(activePinComponents || []) });
      isRightMBClick(event) &&
        onMouseDown?.(event, { action: 'select-pin-components', componentIds: [pinComponent.id] });
    },
    [onMouseDown, pinComponent.id, activePinComponents, isPartOfGroup]
  );

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      event.stopPropagation();
      onTouchStart?.(event, { action: 'move', componentId: pinComponent.id });
    },
    [onTouchStart, pinComponent.id]
  );
  const { handleOpenContextMenu } = usePinEditorContextMenu();
  const handleOnContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      handleOpenContextMenu?.(event, 'pin-component', [pinComponent.id], [pinComponent]);
    },
    [handleOpenContextMenu, pinComponent]
  );

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      onMouseEnter?.(event, { action: 'hover', componentId: pinComponent.id });
    },
    [onMouseEnter, pinComponent.id]
  );

  const handleMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      onMouseLeave?.(event, { action: 'hover', componentId: pinComponent.id });
    },
    [onMouseLeave, pinComponent.id]
  );

  const box = boxFromLayout(pinComponent.layout);

  // Some data props primarily for QA automation
  // Should not be used for styling or logic
  // Should be removed if we have TECH-1 running on test
  const dataProps = {
    [`data-testid`]: pinComponent.type.toLowerCase(),
    ['data-comp-id']: pinComponent.id,
  };

  return (
    <ComponentWrapper
      isLineComponent={isLineLayout(pinComponent.layout)}
      editable={editable}
      style={componentWrapperStyle(isStatic, box, !isHidden, isMoveActionInProgress)}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onContextMenu={handleOnContextMenu}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      active={active}
      highlighted={highlighted}
      showAsAnimated={showAsAnimated}
      {...dataProps}
      className={className}
    >
      {showAsAnimated && !active && (
        <TopAligned>
          <AnimationTag pinComponent={pinComponent} />
        </TopAligned>
      )}
      <Renderer
        pinComponent={pinComponent}
        editable={editable}
        pinId={pin.id}
        pinType={pin.type}
        active={active}
        animationStep={animationStep}
        scale={scale}
        layoutKey={pin.settings.layout}
        layoutParts={layoutParts}
        isThumb={isThumb}
        isTextEditMode={isTextEditMode}
        setModeTextEdit={setModeTextEdit}
        setModeCropping={setModeCropping}
        setModeCategoryEdit={setModeCategoryEdit}
        activeCategoryId={activeCategoryId}
        onMouseDown={onMouseDown}
        onSaveCategory={onSaveCategory}
      />
    </ComponentWrapper>
  );
};

export type PinComponentSwitchRendererProps = {
  pinComponent: PinComponent;
  editable: boolean;
  pinId: string;
  pinType: Pin['type'];
  active: boolean;
  animationStep?: number;
  scale: number;
  layoutKey?: Pin['settings']['layout'];
  layoutParts?: LayoutPartDict;
  isThumb?: boolean;
  isTextEditMode?: boolean;
  setModeTextEdit?(textPinComponentId: string, categoryId?: string): void;
  setModeCropping?(pinComponent: ImagePinComponentType): void;
  setModeCategoryEdit?(categoryAreaPinComponentId: string, categoryId: string): void;
  activeCategoryId?: string;
  onMouseDown?: OnMouseDownHandler;
  onSaveCategory?: (categoryId: string, newText: string) => void;
};

export const PinComponentSwitchDefaultRenderer: React.FC<PinComponentSwitchRendererProps> = (props) => {
  const {
    pinComponent,
    editable,
    pinId,
    active,
    animationStep,
    isThumb,
    scale,
    setModeCropping,
    setModeCategoryEdit,
    activeCategoryId,
    onMouseDown,
  } = props;

  const box = boxFromLayout(pinComponent.layout);
  const type = pinComponent.type;
  switch (type) {
    case 'SHAPE':
      return <ShapePinComponent pinComponent={pinComponent} />;
    case 'HOTSPOT':
      return <HotspotPinComponent pinComponent={pinComponent} active={active} pinId={pinId} />;
    case 'AUDIO':
      return <AudioPinComponent pinComponent={pinComponent} pinId={pinId} isThumb={isThumb} />;
    case 'IMAGE':
      return (
        <ImagePinComponent
          settings={pinComponent.settings}
          layout={box}
          isThumb={isThumb}
          onDoubleClick={() => setModeCropping && setModeCropping(pinComponent)}
        />
      );
    case 'LINE':
      return (
        <LinePinComponent
          componentId={pinComponent.id}
          pinComponent={pinComponent}
          layout={pinComponent.layout as Line}
        />
      );
    case 'TEXT':
      return (
        <TextPinComponent
          componentId={pinComponent.id}
          settings={pinComponent.settings}
          editable={editable}
          pinId={pinId}
          active={active}
          animationStep={animationStep}
        />
      );
    case 'ANSWER_AREA':
      return (
        <AnswerAreaPinComponent settings={pinComponent.settings}>
          <AnswerAreaContentSwitch />
        </AnswerAreaPinComponent>
      );
    case 'CATEGORY_AREA':
      return (
        <CategoryAreaPinComponent
          settings={pinComponent.settings}
          componentId={pinComponent.id}
          pinId={pinId}
          active={active}
          editable={editable}
          scale={scale}
          activeCategoryId={activeCategoryId}
          onMouseDown={onMouseDown}
          setModeCategoryEdit={setModeCategoryEdit}
        />
      );
    case 'DROP_ZONE':
      return (
        <DropZonePinComponent
          componentId={pinComponent.id}
          settings={pinComponent.settings}
          active={active}
          editable={editable}
          pinId={pinId}
          isLinked={false}
        />
      );
    case 'VIDEO':
      return (
        <VideoPinComponent
          settings={pinComponent.settings}
          componentId={pinComponent.id}
          scale={scale}
          width={box.size.width}
          height={box.size.height}
          editable={editable}
          active={active}
          isThumb={isThumb}
        />
      );
    case 'SPINNER':
      return <SpinnerPinComponent layout={pinComponent.layout as Box} settings={pinComponent.settings} />;
    case 'UNSUPPORTED':
      return <UnsupportedPinComponent settings={pinComponent.settings} />;
    case 'TIMER':
      return (
        <TimerPinComponent
          settings={pinComponent.settings}
          timerState="new"
          secondsRemaining={pinComponent.settings.seconds ?? 0}
          disabled={true}
          layout={box}
        />
      );
    case 'UNEDITABLE_SHAPE':
      return (
        <UneditableShapePinComponent settings={pinComponent.settings} width={box.size.width} height={box.size.height} />
      );
    case 'FILL_IN_THE_BLANKS_AREA':
      return (
        <FillInTheBlanksAreaPinComponent
          settings={pinComponent.settings}
          active={active}
          pinId={pinId}
          pinComponentId={pinComponent.id}
        />
      );
    default:
      console.warn(`PinComponentSwitch: unrecognized pin component type: ${type}`);
      return null;
  }
};

export const PinComponentSwitchDefaultRendererMemo = memo(PinComponentSwitchDefaultRenderer);

interface ComponentWrapperProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  isLineComponent: boolean;
  editable: boolean;
  active: boolean;
  highlighted?: boolean;
  showAsAnimated?: boolean;
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onTouchStart?: (event: React.TouchEvent<HTMLDivElement>) => void;
  onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
  'data-testid'?: string;
  'data-comp-id'?: string;
}
function ComponentWrapper(props: ComponentWrapperProps) {
  const { children, ...otherProps } = props;

  return <StyledComponentWrapper {...otherProps}>{children}</StyledComponentWrapper>;
}

type StyledComponentWrapperProps = Pick<
  ComponentWrapperProps,
  'isLineComponent' | 'editable' | 'active' | 'highlighted' | 'showAsAnimated'
>;

const StyledComponentWrapper = styled.div<StyledComponentWrapperProps>`
  position: absolute;

  ${(props: StyledComponentWrapperProps) =>
    !props.editable &&
    css`
      user-select: none;
    `}
  pointer-events: ${(props: StyledComponentWrapperProps) => (props.isLineComponent ? 'none' : 'initial')};

  ${(props: StyledComponentWrapperProps) =>
    props.highlighted &&
    !props.isLineComponent &&
    props.editable &&
    !props.active &&
    css`
      outline: 2px solid ${colorSets.disabled.strokeSubjacent};
    `}

  ${(props: StyledComponentWrapperProps) =>
    props.showAsAnimated &&
    !props.isLineComponent &&
    props.editable &&
    !props.active &&
    css`
      box-shadow:
        0 0 0 2px ${color.state.active.stroke},
        0 0 0 3px ${color.neutralNew.white.fill};
    `}

  ${(props: StyledComponentWrapperProps) =>
    props.active &&
    css`
      &::before {
        content: ' ';
        position: absolute;
        top: -10px;
        left: -10px;
        width: calc(100% + 20px);
        height: calc(100% + 20px);
        z-index: -1;
      }
    `};
`;

/**
 * The pseudo element above is to increase the interaction radius by 10px on each side of the pin component
 * 10px = current grippy size
 */

function componentWrapperStyle(isStatic: boolean, box: Box, visible: boolean, isMoving?: boolean): React.CSSProperties {
  const { position, size, rotation } = box;
  // Non-static components are positioned with transform instead of top/left to avoid layout thrashing while moving
  const transform = [];
  !isStatic && transform.push(`translate(${position.x}px, ${position.y}px)`);
  rotation !== 0 && transform.push(`rotate(${rotation}deg)`);
  const inset = isStatic ? `${position.y}px 0px 0px ${position.x}px` : undefined;

  return {
    transform: transform.join(' '),
    inset,
    width: size.width,
    height: size.height,
    visibility: visible ? 'visible' : 'hidden',
    ...(isMoving ? { cursor: 'move' } : {}),
  };
}
